import * as XLSX from "xlsx";

const IFS_DETAIL_MASTER = {
  C10033: { cnic: "35401-2287713-3", contact: "0321-4987545", joined: "07-04-2022" },
  C10034: { contact: "0302-5683129", joined: "18-12-2023" },
  C10530: { cnic: "41303-1201158-1", contact: "0331-3805025", joined: "07-04-2022" },
  C10322: { joined: "04-12-2024" },
  C10512: { cnic: "31202-9403595-7", contact: "0300-3538823", joined: "01-05-2023" },
  C10656: { joined: "03-01-2026" },
  C10672: { joined: "01-05-2026" }
};

const SEGMENTS = {
  ifs: { label: "IFS FO", filename: /IFS\s*FOs.*Salary\s*Working/i, sheet: /combined\s*salary/i },
  agronomy: { label: "Agronomy", filename: /Agronomy\s*DGs\s*Salary.*Salary\s*Working/i, sheet: /combined\s*salary/i }
};

const clean = (value) => String(value ?? "").trim();
const normalizeCode = (value) => clean(value).replace(/\s+/g, "").toUpperCase();
const trimRow = (row) => Object.fromEntries(Object.entries(row).map(([key, value]) => [key.trim(), value]));

function money(value, field, code) {
  const number = Number(clean(value).replace(/,/g, ""));
  if (!Number.isFinite(number) || number < 0) throw new Error(`${code}: invalid ${field} amount.`);
  return number;
}

function periodFromText(value) {
  const match = clean(value).match(/(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)[- _()]*(20\d{2}|\d{2})/i);
  return match ? `${match[1].slice(0, 3)}-${match[2].slice(-2)}` : "";
}

function identifySegment(fileName, workbook) {
  const byName = Object.entries(SEGMENTS).find(([, config]) => config.filename.test(fileName));
  if (byName) return byName[0];
  if (workbook.SheetNames.some((name) => /^Agronomy$/i.test(name.trim()))) return "agronomy";
  const firstRows = workbook.SheetNames.flatMap((name) => XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: "", raw: false }).slice(0, 4).flat()).join(" ");
  if (/\bIFS\b/i.test(firstRows)) return "ifs";
  throw new Error("The file is not recognized as the standard IFS FO or Agronomy workbook.");
}

function agronomyDetails(workbook) {
  const sheetName = workbook.SheetNames.find((name) => /^Agronomy$/i.test(name.trim()));
  if (!sheetName) throw new Error("Agronomy workbook needs the Agronomy employee-details tab.");
  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "", raw: false });
  const details = new Map();
  for (const row of matrix.slice(4)) {
    const code = normalizeCode(row[1]);
    if (!/^C\d+$/.test(code)) continue;
    if (details.has(code)) throw new Error(`Duplicate Agronomy employee code ${code} in the details tab.`);
    details.set(code, { sonOf: clean(row[3]), cnic: clean(row[4]), contact: clean(row[5]), location: clean(row[7]), joined: clean(row[8]), status: clean(row[10]) });
  }
  return details;
}

export function parseCortevaRecurringWorkbook(buffer, fileName = "") {
  const workbook = XLSX.read(buffer, { type: "array" });
  const segmentKey = identifySegment(fileName, workbook);
  const config = SEGMENTS[segmentKey];
  const sheetName = workbook.SheetNames.find((name) => config.sheet.test(name.trim()));
  if (!sheetName) throw new Error(`${config.label} workbook needs a Combined Salary tab.`);
  const records = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "", raw: false }).map(trimRow);
  const detailMap = segmentKey === "agronomy" ? agronomyDetails(workbook) : new Map();
  const codes = new Set();
  const rows = records.filter((row) => /^C\d+$/i.test(normalizeCode(row.Code))).map((row) => {
    const code = normalizeCode(row.Code);
    if (codes.has(code)) throw new Error(`Duplicate ${config.label} employee code ${code}.`);
    codes.add(code);
    const name = clean(row["Employee Name"]);
    if (!name) throw new Error(`${code}: Employee Name is required.`);
    // A code missing from the details tab (new joiner not added there yet,
    // or a leaver still on payroll for their final month) falls back to
    // whatever the Combined Salary row itself has, same as IFS already does
    // below — headcount changes month to month and shouldn't block the
    // whole invoice over one employee's S/O, CNIC, or status being blank.
    const details = segmentKey === "ifs" ? (IFS_DETAIL_MASTER[code] || {}) : (detailMap.get(code) || {});
    return {
      code, name, designation: clean(row.Designation) || (segmentKey === "ifs" ? "F.O" : "Agronomy DG"),
      sonOf: details?.sonOf || "", cnic: details?.cnic || "", contact: details?.contact || clean(row["Mobile Number"]),
      location: details?.location || clean(row.Territory), joined: details?.joined || clean(row["D.O.J"]),
      status: details?.status || clean(row.Status) || "Active", days: Number(clean(row["Working Days"] || row.Days) || 0),
      salary: money(row.Salary, "Salary", code), fuel: money(row["Fuel Allowance"], "Fuel Allowance", code),
      gli: money(row.GLI, "GLI", code), health: money(row["Health Insurance"], "Health Insurance", code),
      eobi: money(row.EOBI, "EOBI", code), serviceFee: money(row["Service Charges"], "Service Charges", code)
    };
  });
  if (!rows.length) throw new Error(`No valid ${config.label} employee codes were found.`);
  const reimbursement = Math.round(rows.reduce((sum, row) => sum + row.salary + row.fuel + row.gli + row.health + row.eobi, 0) * 100) / 100;
  const serviceCharges = Math.round(rows.reduce((sum, row) => sum + row.serviceFee, 0) * 100) / 100;
  const gst = Math.round(serviceCharges * 0.16);
  const period = periodFromText(fileName) || workbook.SheetNames.map(periodFromText).find(Boolean) || "";
  return { segmentKey, segmentLabel: config.label, rows, period, headCount: rows.length, reimbursement, serviceCharges, gst, total: reimbursement + serviceCharges + gst };
}
